// Mock for react-native-date-picker on web
// Uses HTML5 date/time input

import React, { useState } from 'react';
import { View, TextInput, Modal, TouchableOpacity, Text, StyleSheet } from 'react-native';

// Modal picker keeps the picked value in local state so navigating the
// native calendar (changing months/years) does NOT confirm and close the modal.
// Confirmation only happens when the user taps the Confirm button.
const ModalDatePicker = ({
    date,
    open,
    mode,
    title,
    onConfirm,
    onCancel,
    formatDate,
    formatDateTime,
}) => {
    const [draft, setDraft] = useState(date);

    // Sync the draft to the incoming date each time the modal is (re)opened.
    React.useEffect(() => {
        if (open) setDraft(date);
    }, [open, date]);

    const handleChange = (e) => {
        if (e.target.value) {
            setDraft(new Date(e.target.value));
        }
    };

    return (
        <Modal visible={open} transparent animationType="fade">
            <View style={styles.modalOverlay}>
                <View style={styles.modalContent}>
                    <Text style={styles.title}>{title}</Text>
                    <input
                        type={mode === 'datetime' ? 'datetime-local' : mode}
                        value={mode === 'datetime' ? formatDateTime(draft) : formatDate(draft)}
                        onChange={handleChange}
                        style={styles.input}
                    />
                    <View style={styles.buttonRow}>
                        <TouchableOpacity style={styles.button} onPress={onCancel}>
                            <Text style={styles.buttonText}>Cancel</Text>
                        </TouchableOpacity>
                        <TouchableOpacity
                            style={[styles.button, styles.confirmButton]}
                            onPress={() => onConfirm && onConfirm(draft)}
                        >
                            <Text style={[styles.buttonText, styles.confirmText]}>Confirm</Text>
                        </TouchableOpacity>
                    </View>
                </View>
            </View>
        </Modal>
    );
};

const DatePicker = ({
    date,
    onDateChange,
    mode = 'date',
    modal = false,
    open = false,
    onConfirm,
    onCancel,
    title = 'Select Date',
    ...props
}) => {
    const formatDate = (d) => {
        if (!d) return '';
        const dateObj = new Date(d);
        return dateObj.toISOString().split('T')[0];
    };

    const formatDateTime = (d) => {
        if (!d) return '';
        const dateObj = new Date(d);
        return dateObj.toISOString().slice(0, 16);
    };

    // Non-modal: changes apply immediately
    const handleChange = (e) => {
        const newDate = new Date(e.target.value);
        if (onDateChange) onDateChange(newDate);
        if (onConfirm) onConfirm(newDate);
    };

    if (modal) {
        return (
            <ModalDatePicker
                date={date}
                open={open}
                mode={mode}
                title={title}
                onConfirm={onConfirm}
                onCancel={onCancel}
                formatDate={formatDate}
                formatDateTime={formatDateTime}
            />
        );
    }

    return (
        <View>
            <input
                type={mode === 'datetime' ? 'datetime-local' : mode}
                value={mode === 'datetime' ? formatDateTime(date) : formatDate(date)}
                onChange={handleChange}
                style={{ padding: 10, fontSize: 16, borderRadius: 8, border: '1px solid #ccc' }}
            />
        </View>
    );
};

const styles = StyleSheet.create({
    modalOverlay: {
        flex: 1,
        backgroundColor: 'rgba(0,0,0,0.5)',
        justifyContent: 'center',
        alignItems: 'center',
    },
    modalContent: {
        backgroundColor: 'white',
        borderRadius: 12,
        padding: 20,
        minWidth: 300,
    },
    title: {
        fontSize: 18,
        fontWeight: 'bold',
        marginBottom: 16,
        textAlign: 'center',
    },
    input: {
        padding: 12,
        fontSize: 16,
        borderRadius: 8,
        border: '1px solid #ccc',
        marginBottom: 16,
    },
    buttonRow: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        gap: 12,
    },
    button: {
        paddingVertical: 10,
        paddingHorizontal: 20,
        borderRadius: 8,
    },
    confirmButton: {
        backgroundColor: '#2e7d32',
    },
    buttonText: {
        fontSize: 16,
        color: '#666',
    },
    confirmText: {
        color: 'white',
    },
});

export default DatePicker;
